import { useState } from "react";

import { useTranslation } from "react-i18next";

import {
  BellIcon,
  BellOffIcon,
  PlusIcon,
  SaveIcon,
  SendIcon,
} from "lucide-react";

import type { OpenAPI } from "simplydo/interfaces";
import { RichText } from "simplydo/react/richtext";
import { Button } from "simplydo/react/ui/button";
import { Checkbox } from "simplydo/react/ui/checkbox";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
} from "simplydo/react/ui/dropdown";
import { FormField, FormLabel } from "simplydo/react/ui/form";
import { H5 } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import { TextArea } from "simplydo/react/ui/textarea";

import type { ChallengeQuestion } from "../../types";

type EditableQuestion =
  | ChallengeQuestion
  | (Partial<ChallengeQuestion> & {
      challenge: string;
      question?: string;
      answer?: string;
    });

export const QuestionModal = ({
  question,
  onClose,
  canManageQuestions,
  saveAndMakePublic,
  onSave,
  isSaving,
  isPublishing,
}: {
  question: EditableQuestion;
  onClose: () => void;
  canManageQuestions: boolean;
  saveAndMakePublic: (question: EditableQuestion, notifyUsers: boolean) => void;
  onSave: (questionData: EditableQuestion) => void;
  isSaving: boolean;
  isPublishing: boolean;
}) => {
  const { t } = useTranslation();
  const [questionData, setQuestionData] = useState<EditableQuestion>(question);
  const [hasConfirmed, setHasConfirmed] = useState(canManageQuestions);

  return (
    <Modal open={!!question}>
      <ModalHeader>
        {question._id ? "Edit Question" : "Ask a new question"}
      </ModalHeader>
      <ModalContent>
        {!canManageQuestions ? (
          <>
            <div style={{ marginBottom: 16 }}>
              Before you ask a question, please check if it has already been
              asked and answered. If you have a new question, please provide as
              much context as possible to give the {t("generic.challenge")}{" "}
              managers the best chance of answering your question.
            </div>
            <Checkbox
              toggle
              checked={hasConfirmed}
              onChange={(_e, data) => {
                setHasConfirmed(!!data.checked);
              }}
              style={{ marginBottom: 16 }}
            >
              I confirm that I have read the current list of clarification
              questions and responses, and that my question is new.
            </Checkbox>
          </>
        ) : null}
        <FormField>
          <FormLabel>Question</FormLabel>
          <TextArea
            placeholder={`In detail, what would you like an explanation for? Please provide as much context as possible to give ${t("generic.challenge")} managers the best chance of answering your question.`}
            value={questionData.question ?? ""}
            onChange={(e) => {
              setQuestionData((prev) => ({
                ...prev,
                question: e.target.value,
              }));
            }}
          />
        </FormField>
        {canManageQuestions ? (
          <>
            <H5>
              Answer (only available to {t("generic.challenge")} managers)
            </H5>
            <RichText
              forType="challenge"
              forId={question.challenge}
              emptyReturnValue={""}
              placeholder="Please provide an answer to the question above."
              value={questionData.answer ?? ""}
              onChange={(data) => {
                setQuestionData((prev) => ({ ...prev, answer: data }));
              }}
            />
          </>
        ) : null}
      </ModalContent>
      <ModalActions>
        <Button onClick={() => onClose()}>Close</Button>
        <Button
          disabled={!hasConfirmed}
          loading={isSaving}
          onClick={() => {
            onSave(questionData);
            onClose();
          }}
          color={
            !canManageQuestions || questionData.published
              ? "primary"
              : undefined
          }
        >
          <Icon icon={canManageQuestions ? SaveIcon : PlusIcon} />
          {canManageQuestions ? "Save" : "Ask question"}
        </Button>
        {canManageQuestions && !questionData.published && (
          <Dropdown
            trigger={
              <Button
                style={{ marginLeft: 10 }}
                tooltip={
                  !questionData?.answer &&
                  "A question requires an answer in order to be made public."
                }
                disabled={!hasConfirmed || !questionData?.answer}
                loading={isSaving || isPublishing}
                color="primary"
              >
                <Icon icon={SendIcon} />
                Save and make public
              </Button>
            }
          >
            <DropdownMenu>
              <DropdownItem
                onClick={() => saveAndMakePublic(questionData, true)}
              >
                <Icon icon={BellIcon} />
                Make public and notify users
              </DropdownItem>
              <DropdownItem
                onClick={() => saveAndMakePublic(questionData, false)}
              >
                <Icon icon={BellOffIcon} />
                Make public without notifying users
              </DropdownItem>
            </DropdownMenu>
          </Dropdown>
        )}
      </ModalActions>
    </Modal>
  );
};
